// @ts-nocheck
// Enhanced error handling utilities for template system

export class TemplateError extends Error {
  code: string;
  context: Record<string, unknown>;
  timestamp: Date;

  constructor(message, code, context = {}) {
    super(message);
    this.name = 'TemplateError';
    this.code = code;
    this.context = context;
    this.timestamp = new Date();
  }
}

export class ValidationError extends TemplateError {
  constructor(message, field, value) {
    super(message, 'VALIDATION_ERROR', { field, value });
    this.name = 'ValidationError';
  }
}

export class SaveError extends TemplateError {
  constructor(message, templateId, operation = 'save') {
    super(message, 'SAVE_ERROR', { templateId, operation });
    this.name = 'SaveError';
  }
}

export class ConflictError extends TemplateError {
  constructor(message, conflictDetails) {
    super(message, 'CONFLICT_ERROR', { conflictDetails });
    this.name = 'ConflictError';
  }
}

export class NetworkError extends TemplateError {
  constructor(message, status, url) {
    super(message, 'NETWORK_ERROR', { status, url });
    this.name = 'NetworkError';
  }
}

// Error recovery strategies
export const ErrorRecoveryStrategies = {
  // Auto-save recovery
  RECOVER_FROM_AUTO_SAVE: 'recover_from_auto_save',
  // Version history recovery
  RECOVER_FROM_VERSION: 'recover_from_version',
  // Local storage recovery
  RECOVER_FROM_LOCAL_STORAGE: 'recover_from_local_storage',
  // Conflict resolution
  RESOLVE_CONFLICT: 'resolve_conflict',
  // Retry operation
  RETRY_OPERATION: 'retry_operation',
  // Graceful degradation
  GRACEFUL_DEGRADATION: 'graceful_degradation',
};

export class ErrorRecoveryManager {
  constructor() {
    this.recoveryAttempts = new Map();
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
  }

  // Handle different types of errors with appropriate recovery strategies
  async handleError(error, context = {}) {
    console.error('Template Error:', error);

    const errorKey = this.getErrorKey(error, context);
    const attempts = this.recoveryAttempts.get(errorKey) || 0;

    // Log error for monitoring
    this.logError(error, context, attempts);

    // Determine recovery strategy
    const strategy = this.determineRecoveryStrategy(error, attempts);
    
    if (strategy) {
      try {
        const result = await this.executeRecoveryStrategy(strategy, error, context);
        this.recoveryAttempts.delete(errorKey); // Clear attempts on success
        return result;
      } catch (recoveryError) {
        this.recoveryAttempts.set(errorKey, attempts + 1);
        
        if (attempts < this.maxRetries) {
          // Wait before retry
          await this.delay(this.retryDelay * Math.pow(2, attempts));
          return this.handleError(recoveryError, context);
        } else {
          // Max retries reached, escalate error
          throw new TemplateError(
            'Recovery failed after maximum attempts',
            'RECOVERY_FAILED',
            { originalError: error, recoveryError, attempts }
          );
        }
      }
    }

    // No recovery strategy available, re-throw error
    throw error;
  }

  determineRecoveryStrategy(error, attempts) {
    switch (error.code) {
      case 'SAVE_ERROR':
        if (attempts === 0) return ErrorRecoveryStrategies.RETRY_OPERATION;
        if (attempts === 1) return ErrorRecoveryStrategies.RECOVER_FROM_AUTO_SAVE;
        return ErrorRecoveryStrategies.RECOVER_FROM_LOCAL_STORAGE;

      case 'CONFLICT_ERROR':
        return ErrorRecoveryStrategies.RESOLVE_CONFLICT;

      case 'NETWORK_ERROR':
        if (attempts < 2) return ErrorRecoveryStrategies.RETRY_OPERATION;
        return ErrorRecoveryStrategies.GRACEFUL_DEGRADATION;

      case 'VALIDATION_ERROR':
        return ErrorRecoveryStrategies.GRACEFUL_DEGRADATION;

      default:
        if (attempts === 0) return ErrorRecoveryStrategies.RETRY_OPERATION;
        return ErrorRecoveryStrategies.GRACEFUL_DEGRADATION;
    }
  }

  async executeRecoveryStrategy(strategy, error, context) {
    switch (strategy) {
      case ErrorRecoveryStrategies.RETRY_OPERATION:
        return this.retryOperation(error, context);

      case ErrorRecoveryStrategies.RECOVER_FROM_AUTO_SAVE:
        return this.recoverFromAutoSave(error, context);

      case ErrorRecoveryStrategies.RECOVER_FROM_VERSION:
        return this.recoverFromVersion(error, context);

      case ErrorRecoveryStrategies.RECOVER_FROM_LOCAL_STORAGE:
        return this.recoverFromLocalStorage(error, context);

      case ErrorRecoveryStrategies.RESOLVE_CONFLICT:
        return this.resolveConflict(error, context);

      case ErrorRecoveryStrategies.GRACEFUL_DEGRADATION:
        return this.gracefulDegradation(error, context);

      default:
        throw new TemplateError('Unknown recovery strategy', 'UNKNOWN_STRATEGY');
    }
  }

  async retryOperation(error, context) {
    if (context.operation && typeof context.operation === 'function') {
      return await context.operation();
    }
    throw error;
  }

  async recoverFromAutoSave(error, context) {
    // Try to recover from auto-saved version
    const autoSaveKey = `template_autosave_${context.templateId}`;
    const autoSavedData = localStorage.getItem(autoSaveKey);
    
    if (autoSavedData) {
      try {
        const template = JSON.parse(autoSavedData);
        return {
          recovered: true,
          template,
          message: 'Recovered from auto-save',
          strategy: 'auto_save'
        };
      } catch (parseError) {
        throw new TemplateError('Failed to parse auto-saved data', 'PARSE_ERROR');
      }
    }
    
    throw new TemplateError('No auto-save data available', 'NO_AUTO_SAVE');
  }

  async recoverFromVersion(error, context) {
    // Try to recover from version history
    if (context.versionHistory && context.versionHistory.length > 0) {
      const latestVersion = context.versionHistory[0];
      return {
        recovered: true,
        template: latestVersion.templateSnapshot,
        message: `Recovered from version ${latestVersion.version}`,
        strategy: 'version_history'
      };
    }
    
    throw new TemplateError('No version history available', 'NO_VERSION_HISTORY');
  }

  async recoverFromLocalStorage(error, context) {
    // Try to recover from local storage backup
    const backupKey = `template_backup_${context.templateId}`;
    const backupData = localStorage.getItem(backupKey);
    
    if (backupData) {
      try {
        const template = JSON.parse(backupData);
        return {
          recovered: true,
          template,
          message: 'Recovered from local backup',
          strategy: 'local_storage'
        };
      } catch (parseError) {
        throw new TemplateError('Failed to parse backup data', 'PARSE_ERROR');
      }
    }
    
    throw new TemplateError('No local backup available', 'NO_BACKUP');
  }

  async resolveConflict(error, context) {
    // Provide conflict resolution options
    return {
      needsResolution: true,
      conflictDetails: error.context.conflictDetails,
      options: [
        { id: 'keep_local', label: 'Keep Local Changes', description: 'Keep your changes and discard remote changes' },
        { id: 'keep_remote', label: 'Keep Remote Changes', description: 'Discard your changes and use remote version' },
        { id: 'merge', label: 'Merge Changes', description: 'Attempt to merge both versions' },
        { id: 'create_copy', label: 'Create Copy', description: 'Save your version as a new template' }
      ],
      strategy: 'conflict_resolution'
    };
  }

  async gracefulDegradation(error, context) {
    // Provide graceful degradation options
    return {
      degraded: true,
      message: 'Operating in limited mode due to error',
      limitations: this.getDegradationLimitations(error),
      suggestions: this.getDegradationSuggestions(error),
      strategy: 'graceful_degradation'
    };
  }

  getDegradationLimitations(error) {
    switch (error.code) {
      case 'NETWORK_ERROR':
        return [
          'Auto-save is disabled',
          'Collaboration features unavailable',
          'Template gallery may show cached data',
          'AI assistant features disabled'
        ];
      case 'VALIDATION_ERROR':
        return [
          'Some template features may not work correctly',
          'Publishing is disabled until errors are fixed',
          'Preview may not be accurate'
        ];
      default:
        return [
          'Some features may be limited',
          'Data may not be synchronized',
          'Performance may be affected'
        ];
    }
  }

  getDegradationSuggestions(error) {
    switch (error.code) {
      case 'NETWORK_ERROR':
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Save your work locally',
          'Contact support if the issue persists'
        ];
      case 'VALIDATION_ERROR':
        return [
          'Review and fix validation errors',
          'Check template structure',
          'Ensure all required fields are filled',
          'Remove invalid content'
        ];
      default:
        return [
          'Try refreshing the page',
          'Clear browser cache',
          'Contact support if the issue persists'
        ];
    }
  }

  getErrorKey(error, context) {
    return `${error.code}_${context.templateId || 'unknown'}_${context.operation || 'unknown'}`;
  }

  logError(error, context, attempts) {
    const logData = {
      timestamp: new Date().toISOString(),
      error: {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      },
      context,
      attempts,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // In a real app, this would send to a logging service
    console.error('Template Error Log:', logData);
    
    // Store in local storage for debugging
    const errorLog = JSON.parse(localStorage.getItem('template_error_log') || '[]');
    errorLog.push(logData);
    
    // Keep only last 50 errors
    if (errorLog.length > 50) {
      errorLog.splice(0, errorLog.length - 50);
    }
    
    localStorage.setItem('template_error_log', JSON.stringify(errorLog));
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Clear recovery attempts for a specific error
  clearRecoveryAttempts(error, context) {
    const errorKey = this.getErrorKey(error, context);
    this.recoveryAttempts.delete(errorKey);
  }

  // Get error statistics
  getErrorStats() {
    const errorLog = JSON.parse(localStorage.getItem('template_error_log') || '[]');
    const stats = {
      total: errorLog.length,
      byCode: {},
      byHour: {},
      recent: errorLog.slice(-10)
    };

    errorLog.forEach(log => {
      const code = log.error.code;
      const hour = new Date(log.timestamp).getHours();
      
      stats.byCode[code] = (stats.byCode[code] || 0) + 1;
      stats.byHour[hour] = (stats.byHour[hour] || 0) + 1;
    });

    return stats;
  }
}

// Global error recovery manager instance
export const errorRecoveryManager = new ErrorRecoveryManager();

// Utility functions
export const handleTemplateError = (error, context) => {
  return errorRecoveryManager.handleError(error, context);
};

export const createTemplateError = (message, code, context) => {
  return new TemplateError(message, code, context);
};

export const isRecoverableError = (error) => {
  const recoverableCodes = [
    'SAVE_ERROR',
    'NETWORK_ERROR',
    'CONFLICT_ERROR',
    'VALIDATION_ERROR'
  ];
  return recoverableCodes.includes(error.code);
};

// Retry utility function with exponential backoff
export const withRetry = async (operation, options = {}, onRetry = null) => {
  // Handle different parameter signatures
  let config;
  let retryCallback;
  
  if (typeof options === 'number') {
    // Legacy signature: withRetry(operation, maxRetries, baseDelay)
    config = {
      maxRetries: options,
      baseDelay: onRetry || 1000,
      retryableErrors: []
    };
    retryCallback = null;
  } else {
    // New signature: withRetry(operation, options, callback)
    config = {
      maxRetries: 3,
      baseDelay: 1000,
      retryableErrors: [],
      ...options
    };
    retryCallback = onRetry;
  }
  
  const { maxRetries, baseDelay, retryableErrors } = config;
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Check if this error should trigger a retry
      if (retryableErrors.length > 0) {
        const shouldRetry = retryableErrors.some(errorType => 
          error.message?.includes(errorType) || 
          error.name?.includes(errorType) ||
          error.code?.includes(errorType)
        );
        if (!shouldRetry) {
          throw error;
        }
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      
      // Add some jitter to prevent thundering herd
      const jitter = Math.random() * 0.1 * delay;
      const totalDelay = delay + jitter;
      
      // Call retry callback if provided
      if (retryCallback) {
        retryCallback(attempt + 1, totalDelay, error);
      } else {
        console.warn(`Operation failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${Math.round(totalDelay)}ms:`, error.message);
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, totalDelay));
    }
  }
  
  // If we get here, all retries failed
  throw new TemplateError(
    `Operation failed after ${maxRetries + 1} attempts: ${lastError.message}`,
    'RETRY_EXHAUSTED',
    { originalError: lastError, attempts: maxRetries + 1 }
  );
};

// Format error for user-friendly display
export const formatErrorForDisplay = (error, options = {}) => {
  const {
    showTechnicalDetails = false,
    includeStack = false,
    maxMessageLength = 200
  } = options;

  // Default formatted error object
  const formatted = {
    title: 'An Error Occurred',
    message: 'Something went wrong. Please try again.',
    type: 'error',
    code: null,
    timestamp: new Date(),
    canRetry: false,
    suggestions: [],
    technicalDetails: null
  };

  if (!error) {
    return formatted;
  }

  // Handle different error types
  if (error instanceof TemplateError) {
    formatted.title = getErrorTitle(error.code);
    formatted.message = error.message;
    formatted.code = error.code;
    formatted.canRetry = isRecoverableError(error);
    formatted.suggestions = getErrorSuggestions(error.code);
    
    if (showTechnicalDetails) {
      formatted.technicalDetails = {
        code: error.code,
        context: error.context,
        timestamp: error.timestamp,
        stack: includeStack ? error.stack : null
      };
    }
  } else if (error instanceof Error) {
    // Standard JavaScript Error
    formatted.message = error.message;
    formatted.code = error.name;
    
    // Try to determine if it's retryable based on message
    formatted.canRetry = isNetworkError(error) || isTemporaryError(error);
    formatted.suggestions = getGenericErrorSuggestions(error);
    
    if (showTechnicalDetails) {
      formatted.technicalDetails = {
        name: error.name,
        message: error.message,
        stack: includeStack ? error.stack : null
      };
    }
  } else if (typeof error === 'string') {
    // String error message
    formatted.message = error;
  } else if (error && typeof error === 'object') {
    // Object with error properties
    formatted.message = error.message || error.error || 'Unknown error occurred';
    formatted.code = error.code || error.status;
    formatted.canRetry = error.retryable || false;
  }

  // Truncate message if too long
  if (formatted.message && formatted.message.length > maxMessageLength) {
    formatted.message = formatted.message.substring(0, maxMessageLength - 3) + '...';
  }

  return formatted;
};

// Helper function to get user-friendly error titles
function getErrorTitle(errorCode) {
  const titles = {
    'SAVE_ERROR': 'Save Failed',
    'NETWORK_ERROR': 'Connection Problem',
    'VALIDATION_ERROR': 'Invalid Data',
    'CONFLICT_ERROR': 'Conflict Detected',
    'PERMISSION_ERROR': 'Access Denied',
    'TIMEOUT_ERROR': 'Request Timeout',
    'RETRY_EXHAUSTED': 'Operation Failed',
    'PARSE_ERROR': 'Data Format Error',
    'NOT_FOUND': 'Resource Not Found',
    'SERVER_ERROR': 'Server Error'
  };
  
  return titles[errorCode] || 'Error';
}

// Helper function to get error-specific suggestions
function getErrorSuggestions(errorCode) {
  const suggestions = {
    'SAVE_ERROR': [
      'Check your internet connection',
      'Try saving again',
      'Your work is backed up locally'
    ],
    'NETWORK_ERROR': [
      'Check your internet connection',
      'Try refreshing the page',
      'Contact support if the problem persists'
    ],
    'VALIDATION_ERROR': [
      'Check the highlighted fields',
      'Ensure all required information is provided',
      'Verify data formats are correct'
    ],
    'CONFLICT_ERROR': [
      'Review the conflicting changes',
      'Choose which version to keep',
      'Consider merging the changes'
    ],
    'PERMISSION_ERROR': [
      'Contact your administrator',
      'Check if you have the required permissions',
      'Try logging out and back in'
    ],
    'TIMEOUT_ERROR': [
      'Try the operation again',
      'Check your internet connection',
      'The server may be busy'
    ]
  };
  
  return suggestions[errorCode] || [
    'Try the operation again',
    'Refresh the page if the problem persists',
    'Contact support if you continue to experience issues'
  ];
}

// Helper function to detect network errors
function isNetworkError(error) {
  const networkIndicators = [
    'network',
    'fetch',
    'connection',
    'timeout',
    'offline',
    'unreachable'
  ];
  
  const message = (error.message || '').toLowerCase();
  return networkIndicators.some(indicator => message.includes(indicator));
}

// Helper function to detect temporary errors
function isTemporaryError(error) {
  const temporaryIndicators = [
    'temporary',
    'busy',
    'unavailable',
    'overloaded',
    'rate limit',
    '429',
    '503',
    '504'
  ];
  
  const message = (error.message || '').toLowerCase();
  const code = (error.code || error.status || '').toString();
  
  return temporaryIndicators.some(indicator => 
    message.includes(indicator) || code.includes(indicator)
  );
}

// Helper function to get generic error suggestions
function getGenericErrorSuggestions(error) {
  if (isNetworkError(error)) {
    return [
      'Check your internet connection',
      'Try again in a moment',
      'Refresh the page if the problem persists'
    ];
  }
  
  if (isTemporaryError(error)) {
    return [
      'The service is temporarily busy',
      'Try again in a few minutes',
      'Contact support if the issue continues'
    ];
  }
  
  return [
    'Try the operation again',
    'Refresh the page if needed',
    'Contact support if the problem persists'
  ];
}

// Browser compatibility detection
export const BrowserCompatibility = {
  checkFeatureSupport() {
    return {
      localStorage: typeof Storage !== 'undefined',
      dragAndDrop: 'draggable' in document.createElement('div'),
      fileAPI: typeof FileReader !== 'undefined',
      webWorkers: typeof Worker !== 'undefined',
      indexedDB: 'indexedDB' in window,
      serviceWorker: 'serviceWorker' in navigator,
      webGL: this.hasWebGL(),
      modernCSS: this.hasModernCSS()
    };
  },

  hasWebGL() {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch (e) {
      return false;
    }
  },

  hasModernCSS() {
    return CSS.supports('display', 'grid') && CSS.supports('display', 'flex');
  },

  getUnsupportedFeatures() {
    const support = this.checkFeatureSupport();
    return Object.entries(support)
      .filter(([feature, supported]) => !supported)
      .map(([feature]) => feature);
  },

  getFallbackOptions(unsupportedFeatures) {
    const fallbacks = {
      localStorage: 'Session storage or cookies',
      dragAndDrop: 'Click-based interactions',
      fileAPI: 'Server-side file processing',
      webWorkers: 'Main thread processing (may be slower)',
      indexedDB: 'Local storage with limitations',
      serviceWorker: 'No offline support',
      webGL: 'Canvas 2D rendering',
      modernCSS: 'Legacy CSS layouts'
    };

    return unsupportedFeatures.map(feature => ({
      feature,
      fallback: fallbacks[feature] || 'Feature not available'
    }));
  }
};

// Re-export utility functions from retryMechanism for convenience
export { getErrorMessage, classifyError, getErrorActions } from './retryMechanism';
